/************************************************************************************************
 * Copyright TRUSST AI PTY LTD. All Rights Reserved.                                            *
 *                                                                                              *
 * Licensed under the TRUSST SOFTWARE LICENSE (the "License"). You may not use this file except *
 * in compliance with the "LICENSE" file accompanying this file. This file is distributed       *
 * on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, express or implied.       *
 *                                                                                              *
 * See the "License" file for the specific language governing permissions and limitations       *
 * under the License and limitations under the License.                                         *
 ***********************************************************************************************/

import {
  type GetBatchContactImportResponseContent,
  type GetPromptRevisionResponseContent,
  type ResponseError,
} from '@agent-assist/api-typescript-react-query-hooks';
import {Card, CardContent, Stack, Typography} from '@mui/material';

import Grid from '@mui/material/Grid2';
import type {QueryObserverSuccessResult} from '@tanstack/react-query';
import {useFlags} from 'launchdarkly-react-client-sdk';
import LegacyHeading from './Heading';
import StatusIndicator, {type StatusTypeEnum} from './StatusIndicator';
import type {FeatureFlagSet} from '../flags';
import {type ProgressCounts} from '../pages/ContactImportViewPage';
import {formatDateCommon} from '../services/dates';
import { LoadingSpinner } from './LoadingSpinner';

interface ContactsImportHeaderProps {
  contactImport: QueryObserverSuccessResult<GetBatchContactImportResponseContent, ResponseError>;
  contactImportId: string;
  liveProgress: ProgressCounts;
  promptRevisionData?: GetPromptRevisionResponseContent;
  showLive: boolean;
  totalProcessed: number;
  totalFailedContacts: number;
  totalCompletedContacts: number;
  loading: boolean;
}

export const ContactsImportHeader = ({
  contactImport,
  contactImportId,
  totalProcessed,
  totalFailedContacts,
  totalCompletedContacts,
  loading,
}: ContactsImportHeaderProps) => {
  const {bucket, createdBy, createdAt, key, status} = contactImport.data;
  const {functionImportStatus} = useFlags<FeatureFlagSet>();

  return (
    <Grid container spacing={4} columns={functionImportStatus ? 3 : 2} sx={{display: 'flex', alignItems: 'stretch'}}>
      {functionImportStatus && (
        <Grid size={1} sx={{display: 'flex'}}>
          <Card sx={{flex: 1}}>
            <CardContent>
              <Stack spacing={2}>
                <Stack direction={'row'} spacing={2}>
                  <LegacyHeading h3>Status:</LegacyHeading>
                  <StatusIndicator status={status as StatusTypeEnum} />
                </Stack>

                {loading ? (
                  <LoadingSpinner size='sm' />
                ) : (
                  <Stack direction={'row'} spacing={2}>
                    <Stack direction={'row'} spacing={1}>
                      <LegacyHeading h3>Total:</LegacyHeading>
                      <Typography variant={'h6'} sx={{color: 'text.secondary'}}>
                        {totalProcessed || 0}
                      </Typography>
                    </Stack>
                    <Stack direction={'row'} spacing={1}>
                      <LegacyHeading h3>Completed:</LegacyHeading>
                      <Typography variant={'h6'} sx={{color: 'success.main'}}>
                        {totalCompletedContacts || 0}
                      </Typography>
                    </Stack>
                    <Stack direction={'row'} spacing={1}>
                      <LegacyHeading h3>Failed:</LegacyHeading>
                      <Typography variant={'h6'} sx={{color: 'error.main'}}>
                        {totalFailedContacts || 0}
                      </Typography>
                    </Stack>
                  </Stack>
                  
                )}
                </Stack>
            </CardContent>
          </Card>
        </Grid>
      )}

      <Grid size={1} sx={{display: 'flex'}}>
        <Card sx={{flex: 1}}>
          <CardContent>
            <Stack spacing={2}>
              <Stack spacing={1}>
                <LegacyHeading h3>S3 Location:</LegacyHeading>
                <p>{`s3://${bucket}/${key ?? ''}`}</p>
              </Stack>
              <Stack spacing={1}>
                <LegacyHeading h3>Contact Import ID:</LegacyHeading>
                <p>{contactImportId}</p>
              </Stack>
            </Stack>
          </CardContent>
        </Card>
      </Grid>

      <Grid size={1} sx={{display: 'flex'}}>
        <Card sx={{flex: 1}}>
          <CardContent>
            <Stack spacing={2}>
              <Stack direction={'row'} spacing={1}>
                <LegacyHeading h3>Created by:</LegacyHeading>
                <p>{createdBy.username}</p>
              </Stack>
              <Stack direction={'row'} spacing={1}>
                <LegacyHeading h3>Created at:</LegacyHeading>
                <p>{formatDateCommon(createdAt)}</p>
              </Stack>
            </Stack>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
};
